import Box from "@mui/material/Box";
import Typography from "@mui/material/Typography";
import { makeStyles, createStyles } from "@mui/styles";
import {
  Alert,
  AlertTitle,
  Button,
  Chip,
  Collapse,
  debounce,
  Grid,
  Paper,
  Stack,
  SxProps,
  Tab,
  Tabs,
  TextField,
  Tooltip,
} from "@mui/material";
import React, {
  useCallback,
  useEffect,
  useState,
  useLayoutEffect,
  useRef,
} from "react";
import theme from "../../../components/theme/theme";
import { useParams } from "react-router-dom";
import { useAppDispatch, useAppSelector } from "../../../common/store/store";
import { getUserInputForStepId } from "../../../components/wizard/util";
import {
  Parameter,
  ParameterValue,
  SelectionAlternative,
} from "../../../common/types/parameters";
import {
  calculateOutput,
  wasCalculationAborted,
} from "../../../api/module-api";
import DetailsChart from "../detaljer/graph";
import {
  getParameterStates,
  setUserInput as setModuleUserInput,
  resetUserInputs as resetModuleUserInput,
  deviatesFromDefaults as moduleInputDeviatesFromDefaults,
} from "../../../common/store/module-parameters-slice";
import {
  getDisplayValue,
  getType,
  parseAsSelectionAlternative,
} from "../../../components/data-presentation/util";
import "../module.css";
import useAPIError from "../../../common/hooks/useAPIError";
import { getLabel } from "../../../api/label-api";
import { useReactToPrint } from "react-to-print";
import PrintIcon from "@mui/icons-material/Print";
import { ModuleInfo } from "../../../common/types/modules";
import useWizard from "../../../common/hooks/useWizard";
import DistrictHeatingNewPrice from "./district-heating-new-price-section";
import useHeatingTechnologySelection from "../../../common/hooks/useHeatingTechnologySelection";
import HideableChip from "../../../components/navigation/hideable-chip";
import { isCalculated } from "../../../common/util/heating-technology-visibility";
import { isDistrictHeatingSelectedAsSupplementalHeatingSource } from "../supplemental-heating.util";
import { SupplementalHeatingSource } from "../../../common/types/values";

const useStyles = makeStyles(() =>
  createStyles({
    card: {
      padding: "15px",
    },
  })
);

const moduleId = "pa";
const paperElevation = 3;

export default function PriceAdjustments(params: { sx?: SxProps }) {
  let { customer } = useParams();
  let { economicProfile } = useParams();
  const { addError, deleteError } = useAPIError();
  const {
    steps: wizardSteps,
    updateParameterValue: updateWizardParameterValue,
    isWizardParameter,
    wizardIsCompleted,
    userInputDeviatesFromDefaults: wizardUserInputDeviatesFromDefaults,
    resetToDefaults: resetWizardUserInput,
  } = useWizard(customer!, economicProfile!);

  //printing
  const [toPrint, setToPrint] = useState<boolean>(false);
  const [labelTitle, setLabelTitle] = useState<string>("");
  const componentRef = useRef(null);
  const handlePrint = useReactToPrint({
    content: () => componentRef.current,
  });

  const moduleInfo: Omit<ModuleInfo, "accessibleToUser"> = {
    id: moduleId,
    displayName: "Prisjustering",
  };

  let moduleParameterInputs = useAppSelector((state) =>
    getParameterStates(state, {
      customer: customer,
      economicProfile: economicProfile,
    })
  );

  const [outputParameters, setOutputParameters] = useState<Parameter[]>([]);

  const includeVat: boolean = JSON.parse(
    outputParameters.find((parameter) => parameter.id === "VATSelection")
      ?.value ?? "false"
  );

  let deviatesFromDefaults = useAppSelector(
    (state) =>
      wizardUserInputDeviatesFromDefaults() ||
      moduleInputDeviatesFromDefaults(state, {
        customer: customer,
        economicProfile: economicProfile,
      })
  );

  /*Detta tar om alla tabbarna + titlarna i graften
  districtHeatingLabels innehåller title + description*/
  const [districtHeatingLabels, setDistrictHeatingLabels] =
    useState<Parameter>();
  const [groundSourceHeatPumpLabels, setGroundSourceHeatPumpLabels] =
    useState<Parameter>();
  const [airSourceHeatPumpLabels, setAirSourceHeatPumpLabels] =
    useState<Parameter>();
  const [exhaustAirHeatPumpLabels, setExhaustAirHeatPumpLabels] =
    useState<Parameter>();
  const [pelletLabels, setPelletLabels] = useState<Parameter>();

  const { isHidden, switchVisibilitySetting } = useHeatingTechnologySelection();

  const heatingTechnologies = (toPrint: boolean) =>
    [
      {
        id: "DistrictHeatingAdjusted",
        title: "Justerad kostnad",
      },
      {
        id: "DistrictHeating",
        title: districtHeatingLabels?.displayName || "Fjärrvärme",
      },
      {
        id: "GroundSource",
        title: groundSourceHeatPumpLabels?.displayName || "Bergvärme",
      },
      {
        id: "AirSource",
        title: airSourceHeatPumpLabels?.displayName || "Luft/vatten-värmepump",
      },
      {
        id: "ExhaustAir",
        title: exhaustAirHeatPumpLabels?.displayName || "Frånluftsvärmepump",
      },
      {
        id: "Pellet",
        title: pelletLabels?.displayName || "Pellets",
      },
    ].filter((tech) => isCalculated(tech, outputParameters));

  //De aktuella tabbar
  const sections = (toPrint: boolean) => [
    {
      id: "DistrictHeating",
      title: districtHeatingLabels?.displayName || "Fjärrvärme",
      component: (
        <DistrictHeatingNewPrice
          description={districtHeatingLabels?.description ?? undefined}
          parameters={outputParameters}
          includeVat={includeVat}
          onValidParameterChange={onValidParameterChange}
          printTitle={districtHeatingLabels?.displayName || "Fjärrvärme"}
          toPrint={toPrint}
        />
      ),
    },
  ];

  const [activeTab] = useState("DistrictHeating");

  const dispatch = useAppDispatch();

  function onValidParameterChange(id: string, value: string) {
    if (isWizardParameter(id)) {
      let parameter = outputParameters.find((parameter) => parameter.id === id);

      if (parameter !== null && parameter !== undefined) {
        saveWizardParameter(
          id,
          value,
          getDisplayValue(
            value,
            includeVat,
            getType(parameter),
            parameter?.stylingConfig,
            true
          ) ?? value
        );
      }
    } else {
      saveModuleParameter(id, value);
    }
  }

  function saveWizardParameter(
    id: string,
    value: string,
    formattedValue: string
  ) {
    updateWizardParameterValue({ id, value }, formattedValue);
  }

  function saveModuleParameter(id: string, value: string) {
    dispatch(
      setModuleUserInput({
        id: id,
        value: value,
        profile: { customer: customer, economicProfile: economicProfile },
      })
    );
  }

  const calculateOutputDelayed = useCallback(
    debounce(
      (inputs: ParameterValue[], callback: (output: Parameter[]) => void) => {
        calculateOutput(moduleId, inputs, customer, economicProfile)
          .then((calculationResult) => {
            if (wasCalculationAborted(calculationResult)) {
              return;
            }

            callback(calculationResult);
          })
          .catch(() => addError("Error"));
      },
      600
    ),
    [customer, economicProfile]
  );

  useEffect(() => {
    if (wizardIsCompleted) {
      let inputs = wizardSteps
        .flatMap((step) => step.inputParameters)
        .map(
          (parameter) =>
            ({ id: parameter.id, value: parameter.value }) as ParameterValue
        )
        .concat(moduleParameterInputs);
      calculateOutputDelayed(inputs, (parameters) => {
        deleteError();
        setOutputParameters(parameters);
      });
    }
  }, [
    moduleParameterInputs,
    customer,
    economicProfile,
    addError,
    deleteError,
    wizardIsCompleted,
    wizardSteps,
    calculateOutputDelayed,
  ]);

  useEffect(() => {
    let districtValue = getUserInputForStepId(
      "DistrictSelected",
      wizardSteps
    )?.at(0)?.value;
    let buildingValue = getUserInputForStepId(
      "BuildingProfile",
      wizardSteps
    )?.at(0)?.value;
    const district = parseAsSelectionAlternative(districtValue)?.value;
    const building = parseAsSelectionAlternative(buildingValue)?.value;
    if (district !== undefined && building !== undefined) {
      getLabel(
        "DistrictHeatingPriceAdjustmentTab",
        customer,
        district,
        building,
        economicProfile
      ).then((response) => {
        setDistrictHeatingLabels(response);
      });
      getLabel(
        "GroundSourceHeatPumpTab",
        customer,
        district,
        building,
        economicProfile
      ).then((response) => {
        setGroundSourceHeatPumpLabels(response);
      });
      getLabel(
        "AirSourceHeatPumpTab",
        customer,
        district,
        building,
        economicProfile
      ).then((response) => {
        setAirSourceHeatPumpLabels(response);
      });
      getLabel(
        "ExhaustAirHeatPumpTab",
        customer,
        district,
        building,
        economicProfile
      ).then((response) => {
        setExhaustAirHeatPumpLabels(response);
      });
      getLabel("PelletTab", customer, district, building, economicProfile).then(
        (response) => {
          setPelletLabels(response);
        }
      );
    }
  }, [customer, economicProfile, wizardSteps]);

  function resetInput() {
    resetWizardUserInput();
    dispatch(
      resetModuleUserInput({
        profile: { customer: customer, economicProfile: economicProfile },
      })
    );
  }

  let electricBoilerSupplementalHeatingAlternative = (
    outputParameters.find(
      (parameter) => parameter.id === "SupplementalHeatingSource"
    )?.constraints as SelectionAlternative[]
  )?.find(
    (alternative) =>
      alternative.value ===
      JSON.stringify(SupplementalHeatingSource.ElectricBoiler)
  );
  function setSupplementalHeatingSourceToElectricBoiler() {
    onValidParameterChange(
      "SupplementalHeatingSource",
      JSON.stringify(electricBoilerSupplementalHeatingAlternative)
    );
  }

  const classes = useStyles(theme);

  const [isSmallGraphVisible, setIsSmallGraphVisible] =
    useState<boolean>(false);
  const scrollRef = useRef<HTMLDivElement>(null);
  const setScroll = () => {
    let bounds = scrollRef?.current?.getBoundingClientRect();

    setIsSmallGraphVisible(
      bounds?.y !== undefined &&
        bounds?.y !== null &&
        bounds?.height > 0 &&
        bounds?.y < -(bounds.height * 0.2) // show small graph when at least 20% of the big one is hidden
    );
  };

  useLayoutEffect(() => {
    window.addEventListener("scroll", setScroll, true);
    return () => {
      window.removeEventListener("scroll", setScroll);
    };
  }, [scrollRef]);

  const onPrint = () => {
    setToPrint(true);
    setTimeout(function () {
      handlePrint();
    }, 1000);
    setTimeout(function () {
      setToPrint(false);
    }, 1100);
  };

  return (
    <Box key={`${moduleId}-module-container`} sx={params.sx}>
      <Paper
        className={classes.card}
        elevation={paperElevation}
        sx={{
          marginBottom: "40px",
          paddingLeft: 4,
          paddingRight: 4,
          paddingBottom: 4,
          textAlign: "center",
        }}
      >
        <TextField
          id="printed-title"
          label="Dokumentbeskrivning"
          sx={{
            width: "400px",
            maxWidth: "100%",
            paddingRight: 4,
          }}
          placeholder="Här kan du namnge din beräkning"
          onChange={(e) => setLabelTitle(e.target.value)}
          value={labelTitle}
          variant="standard"
          type="text"
        />
        <Button
          variant="contained"
          startIcon={<PrintIcon />}
          onClick={() => onPrint()}
          sx={{ verticalAlign: "bottom", marginTop: 1 }}
        >
          Skriv ut
        </Button>
      </Paper>

      <Typography variant="h1">{moduleInfo.displayName}</Typography>
      <Grid container spacing={2}>
        <Grid item xs={12}>
          <Paper className={classes.card} elevation={paperElevation}>
            <Typography paragraph>
              Nedan presenteras ett diagram som jämför de nuvarande kostnaderna
              för de olika uppvärmningsalternativen med den justerade
              kostnadsbilden för fjärrvärme. Under detta diagram finns det
              möjlighet att justera kostnaderna för fjärrvärme. Information om
              olika parametrar fås genom att använda informationssymbolen vid
              aktuell parameter.
            </Typography>
          </Paper>
        </Grid>
        <Grid ref={scrollRef} item xs={12}>
          <Typography variant="h2">Total värmekostnad per år</Typography>
          <Paper className={classes.card} elevation={paperElevation}>
            <Stack
              direction="row"
              spacing={2}
              justifyContent={"center"}
              marginBottom={2}
            >
              {heatingTechnologies(false).map((heatingTechnology) =>
                heatingTechnology.id.startsWith("DistrictHeating") ? ( // In this view, it is essential to show district heating
                  <Tooltip title="Fjärrvärme kan inte döljas i denna vy">
                    <Chip label={heatingTechnology.title} />
                  </Tooltip>
                ) : (
                  <HideableChip
                    value={heatingTechnology.id}
                    label={heatingTechnology.title}
                    visible={!isHidden(heatingTechnology.id)}
                    onSwitchVisibility={switchVisibilitySetting}
                  />
                )
              )}
            </Stack>
            <DetailsChart
              toPrint={false}
              heatingTechnologies={heatingTechnologies(false).map(
                (heatingTechnology) => ({
                  id: heatingTechnology.id,
                  title: heatingTechnology.title,
                  hidden:
                    isHidden(heatingTechnology.id) &&
                    !heatingTechnology.id.startsWith("DistrictHeating"), // In this view, it is essential to show district heating
                })
              )}
              parameters={outputParameters}
              displayLegends
              includeVat={includeVat}
            />
            <Typography paragraph>
              I diagrammet ovan framgår uppvärmningskostnaden för de olika
              alternativen uppdelat på ett antal olika poster. Du kan släcka och
              tända olika kostnadsposter genom att klicka på förklaringstexterna
              för respektive kostnadspost under grafen.
            </Typography>
          </Paper>
        </Grid>
        <Grid item xs={12}>
          <Stack spacing={1}>
            <Collapse
              in={isDistrictHeatingSelectedAsSupplementalHeatingSource(
                outputParameters
              )}
            >
              <Alert
                severity="info"
                action={
                  electricBoilerSupplementalHeatingAlternative && (
                    <Button
                      color="inherit"
                      size="small"
                      onClick={setSupplementalHeatingSourceToElectricBoiler}
                    >
                      Byt till{" "}
                      {electricBoilerSupplementalHeatingAlternative.displayName}{" "}
                      som spetskälla
                    </Button>
                  )
                }
              >
                <AlertTitle>
                  Observera att kostnaden för fjärrvärmespetsarna är baserade på
                  de nuvarande kostnaderna, inte de justerade.
                </AlertTitle>
              </Alert>
            </Collapse>
            <Collapse in={deviatesFromDefaults}>
              <Alert
                severity="info"
                action={
                  <Button color="inherit" size="small" onClick={resetInput}>
                    Återställ till standardvärden
                  </Button>
                }
              >
                <AlertTitle>
                  Beräkningarna bygger på manuellt justerade värden.
                </AlertTitle>
              </Alert>
            </Collapse>
          </Stack>
        </Grid>
        <Grid item xs={12}>
          <Tabs value={activeTab} variant="scrollable" scrollButtons={true}>
            {sections(false).map((heatingTechnology) => (
              <Tab
                key={`${moduleId}-module-${heatingTechnology.id}`}
                value={heatingTechnology.id}
                label={heatingTechnology.title}
                wrapped
              />
            ))}
          </Tabs>
          {
            sections(false).find(
              (heatingTechnology) => activeTab === heatingTechnology.id
            )?.component
          }
        </Grid>
      </Grid>
      <div
        className={isSmallGraphVisible ? "smallGraph" : "smallGraphInvisible"}
      >
        <DetailsChart
          toPrint={false}
          heatingTechnologies={heatingTechnologies(false).map(
            (heatingTechnology) => ({
              id: heatingTechnology.id,
              title: heatingTechnology.title,
              hidden:
                isHidden(heatingTechnology.id) &&
                !heatingTechnology.id.startsWith("DistrictHeating"), // In this view, it is essential to show district heating
            })
          )}
          parameters={outputParameters}
          includeVat={includeVat}
          displayLegends={false}
        />
      </div>
      {toPrint && (
        <div ref={componentRef} id="toPrint">
          <h1>
            {labelTitle} {new Date().toLocaleString()}
          </h1>
          <Typography variant="h2">Total värmekostnad per år</Typography>
          <DetailsChart
            heatingTechnologies={heatingTechnologies(false).map(
              (heatingTechnology) => ({
                id: heatingTechnology.id,
                title: heatingTechnology.title,
                hidden:
                  isHidden(heatingTechnology.id) &&
                  !heatingTechnology.id.startsWith("DistrictHeating"), // In this view, it is essential to show district heating
              })
            )}
            parameters={outputParameters}
            displayLegends
            includeVat={includeVat}
            toPrint={true}
          />
          <Typography paragraph>
            I diagrammet ovan framgår uppvärmningskostnaden för de olika
            alternativen uppdelat på ett antal olika poster.
          </Typography>
          {isDistrictHeatingSelectedAsSupplementalHeatingSource(
            outputParameters
          ) && (
            <Typography paragraph sx={{ fontWeight: "bold" }}>
              Observera att kostnaden för fjärrvärmespetsarna är baserade på de
              nuvarande kostnaderna, inte de justerade.
            </Typography>
          )}
          {sections(true).map((section, i) => (
            <div className="section">{section.component}</div>
          ))}
          <div></div>
        </div>
      )}
    </Box>
  );
}
